import { preloadSceneAssets } from './render/assets';
import { drawAttacker, drawKeeper } from './render/actors';
import { drawSceneBackground } from './render/background';
import {
  drawBall,
  drawGoalPulse,
  drawKickFlash,
  drawNetRipple,
  drawOutcomeWave,
  drawParticles,
  drawPreviewTargets,
} from './render/effects';
import { getSceneSnapshot } from './engine';
import { RenderProfile, getCanvasScale } from './quality';
import { createStaticLayerCache, StaticLayerCache } from './staticLayerCache';
import { EngineState } from './types';

export interface SizedCanvas {
  context: CanvasRenderingContext2D;
  width: number;
  height: number;
  scale: number;
}

export type SceneRenderCache = StaticLayerCache<HTMLCanvasElement>;

export const createSceneRenderCache = (): SceneRenderCache =>
  createStaticLayerCache<HTMLCanvasElement>();

export const ensureCanvasContext = (
  canvas: HTMLCanvasElement,
  profile: RenderProfile,
  measuredWidth = canvas.clientWidth,
  measuredHeight = canvas.clientHeight,
): SizedCanvas | null => {
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) return null;

  const width = Math.max(1, measuredWidth);
  const height = Math.max(1, measuredHeight);
  const scale = getCanvasScale(width, height, window.devicePixelRatio || 1, profile);
  const backingWidth = Math.max(1, Math.round(width * scale));
  const backingHeight = Math.max(1, Math.round(height * scale));

  if (canvas.width !== backingWidth || canvas.height !== backingHeight) {
    canvas.width = backingWidth;
    canvas.height = backingHeight;
  }

  context.setTransform(scale, 0, 0, scale, 0, 0);
  context.imageSmoothingEnabled = profile.quality === 'full';
  return { context, width, height, scale };
};

export { preloadSceneAssets };

interface SceneLayer {
  depth: number;
  paint: () => void;
}

const buildStaticLayer = (width: number, height: number, scale: number) => {
  const layer = document.createElement('canvas');
  layer.width = Math.max(1, Math.round(width * scale));
  layer.height = Math.max(1, Math.round(height * scale));
  const layerContext = layer.getContext('2d', { alpha: false });
  if (layerContext) {
    layerContext.setTransform(scale, 0, 0, scale, 0, 0);
    drawSceneBackground(layerContext, width, height, 0);
  }
  return layer;
};

export const drawScene = (
  sized: SizedCanvas,
  engine: EngineState,
  nowMs: number,
  profile: RenderProfile,
  staticCache: SceneRenderCache,
) => {
  const { context: ctx, width, height, scale } = sized;
  const snapshot = getSceneSnapshot(engine, nowMs);
  const goalPulse =
    engine.phase === 'result' && engine.shot?.outcome === 'goal'
      ? 1 - snapshot.celebrationProgress * 0.82
      : 0;
  const baseShake =
    engine.phase === 'result' && engine.shot
      ? (1 - snapshot.celebrationProgress) *
        (engine.shot.outcome === 'goal'
          ? engine.shot.perfect
            ? 8
            : 5.5
          : engine.shot.outcome === 'save'
            ? 3.5
            : 2.4)
      : 0;
  const impactShake = baseShake * profile.shakeScale;

  ctx.clearRect(0, 0, width, height);
  ctx.save();
  if (impactShake > 0) {
    ctx.translate(Math.sin(nowMs / 36) * impactShake, Math.cos(nowMs / 52) * impactShake * 0.55);
  }

  const staticKey = `${width.toFixed(1)}x${height.toFixed(1)}@${scale.toFixed(3)}:${profile.quality}`;
  const staticLayer = staticCache.ensure(staticKey, () => buildStaticLayer(width, height, scale));
  ctx.drawImage(staticLayer, 0, 0, width, height);

  drawPreviewTargets(
    ctx,
    width,
    height,
    engine.phase,
    engine.aim,
    snapshot.power,
    nowMs,
    profile,
    'guides',
  );
  drawGoalPulse(
    ctx,
    width,
    height,
    engine.shot?.outcome === 'goal' ? engine.shot.target : null,
    goalPulse,
    profile,
  );
  drawKickFlash(ctx, width, height, engine.phase, snapshot.phaseElapsedMs, profile);

  const layers: SceneLayer[] = [
    {
      depth: snapshot.keeper.z,
      paint: () =>
        drawKeeper(
          ctx,
          width,
          height,
          snapshot.keeper,
          snapshot.flightProgress,
          engine.shot?.keeperAction ?? null,
          engine.shot?.keeperLane ?? null,
        ),
    },
    {
      depth: snapshot.attacker.z,
      paint: () =>
        drawAttacker(ctx, width, height, snapshot.attacker, engine.phase, snapshot.phaseElapsedMs),
    },
    {
      depth: snapshot.ball.z,
      paint: () =>
        drawBall(
          ctx,
          width,
          height,
          snapshot.ball,
          snapshot.flightProgress,
          engine.shot?.power,
          engine.phase === 'flight' || engine.phase === 'result',
          profile,
        ),
    },
  ];

  layers
    .sort((left, right) => right.depth - left.depth)
    .forEach((layer) => layer.paint());
  drawPreviewTargets(
    ctx,
    width,
    height,
    engine.phase,
    engine.aim,
    snapshot.power,
    nowMs,
    profile,
    'reticle',
  );
  drawNetRipple(ctx, width, height, engine.shot, snapshot.celebrationProgress, profile);
  drawOutcomeWave(ctx, width, height, engine.shot, snapshot.celebrationProgress, profile);
  drawParticles(ctx, width, height, engine, profile);
  ctx.restore();

  const vignette = ctx.createLinearGradient(0, height * 0.16, 0, height);
  vignette.addColorStop(0, 'rgba(2,6,23,0)');
  vignette.addColorStop(1, 'rgba(2,6,23,0.36)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, width, height);
};
